import Moment from 'moment-timezone'

export type VendorPricingAgreement = {
  id: string
  scope: VendorPricingAgreementScope
  serviceTypes: VendorPricingAgreementServiceType[]
  vendorId: string | null
  hqId: number | null
  discountType: VendorPricingAgreementDiscountType
  tiers: VendorPricingAgreementTier[]
  active: boolean
  lastUpdatedBy?: string
  createdAt?: Moment.Moment
  updatedAt?: Moment.Moment
}

export type VendorPricingAgreementDiscountType = 'percentage' | 'fixed_price'
export const VendorPricingAgreementDiscountTypes: Record<
  string,
  VendorPricingAgreementDiscountType
> = {
  PERCENTAGE: 'percentage',
  FIXED_PRICE: 'fixed_price',
}
export const DiscountTypeToString = (
  discountType: VendorPricingAgreementDiscountType,
) => {
  switch (discountType) {
    case VendorPricingAgreementDiscountTypes.PERCENTAGE:
      return 'Percentage'
    case VendorPricingAgreementDiscountTypes.FIXED_PRICE:
      return 'Fixed Price'
    default:
      return 'Unknown'
  }
}

export type VendorPricingAgreementScope =
  | 'universal'
  | 'market'
  | 'vendor_market'
export const VendorPricingAgreementScopes: Record<
  string,
  VendorPricingAgreementScope
> = {
  UNIVERSAL: 'universal',
  MARKET: 'market',
  VENDOR_MARKET: 'vendor_market',
}

export type VendorPricingAgreementServiceType = 'catering' | 'group_order'
export const VendorPricingAgreementServiceTypes: Record<
  string,
  VendorPricingAgreementServiceType
> = {
  CATERING: 'catering',
  GROUP_ORDER: 'group_order',
}
export const ServiceTypeToString = (
  serviceType: VendorPricingAgreementServiceType,
) => {
  switch (serviceType) {
    case VendorPricingAgreementServiceTypes.CATERING:
      return 'Catering'
    case VendorPricingAgreementServiceTypes.GROUP_ORDER:
      return 'Group Order'
    default:
      return 'Unknown'
  }
}

export type VendorPricingAgreementTier = {
  lowerLimit: number
  upperLimit: number | null
  discountPercentage?: number
  fixedEntreePrice?: number
  fixedSidePrice?: number
  position: number
}

export type ApiVendorPricingAgreement = {
  id: string
  scope: VendorPricingAgreementScope
  serviceTypes: VendorPricingAgreementServiceType[]
  vendorId: string | null
  hqId: number | null
  discountType: VendorPricingAgreementDiscountType
  tiers: ApiVendorPricingAgreementTier[]
  active: boolean
  lastUpdatedBy?: string
  createdAt: string
  updatedAt: string
}

export type ApiVendorPricingAgreementTier = {
  lowerLimit: string
  upperLimit: string | null
  discountPercentage?: string
  fixedEntreePrice?: string
  fixedSidePrice?: string
  position: number
}

export const apiVendorPricingAgreementToVendorPricingAgreement = (
  apiVendorPricingAgreement: ApiVendorPricingAgreement,
): VendorPricingAgreement => {
  const { tiers, createdAt, updatedAt, ...rest } = apiVendorPricingAgreement

  const out = {
    ...rest,
    createdAt: Moment(createdAt),
    updatedAt: Moment(updatedAt),
    tiers: tiers.map(apiVendorPricingAgreementTierToVendorPricingAgreementTier),
  }

  return out
}

export const apiVendorPricingAgreementTierToVendorPricingAgreementTier = (
  apiVendorPricingAgreementTier: ApiVendorPricingAgreementTier,
): VendorPricingAgreementTier => {
  const {
    lowerLimit,
    upperLimit,
    discountPercentage,
    fixedEntreePrice,
    fixedSidePrice,
    position,
  } = apiVendorPricingAgreementTier

  const out: VendorPricingAgreementTier = {
    lowerLimit: Number(lowerLimit),
    upperLimit: upperLimit ? Number(upperLimit) : null,
    position,
  }

  if (discountPercentage !== undefined) {
    out.discountPercentage = Number(discountPercentage)
  }
  if (fixedEntreePrice !== undefined) {
    out.fixedEntreePrice = Number(fixedEntreePrice)
  }
  if (fixedSidePrice !== undefined) {
    out.fixedSidePrice = Number(fixedSidePrice)
  }

  return out
}

export type SearchVendorPricingAgreementsParams = {
  ids?: string[]
  scope?: VendorPricingAgreementScope
  scopes?: VendorPricingAgreementScope[]
  vendorId?: string
  vendorIds?: string[]
  hqId?: number
  hqIds?: number[]
  active?: boolean
  offset: number
  limit: number
}
